import { runInAction } from 'mobx';
import Action from '../../../decorators/Action';
import Observable from '../../../decorators/Observable';
import AbstractSessionModel from '../../../models/AbstractSessionModel';
import Api from '../../../services/Api';
import ImageWebsiteLogo, { ColorScheme } from '../view/files/ImageWebsiteLogo';
import FoundWebsiteLogo from '../view/files/FoundWebsiteLogo';
import Computed from '../../../decorators/Computed';
import ColorThemeStateMachine from './ColorThemeStateMachine';
import StoreFieldInSession from '../../../decorators/StoreFieldInSession';
import UploadedWebsiteLogo from '../view/files/UploadedWebsiteLogo';
import DesignModelWizardData from './DesignModelWizardData';
import { IWizardDataModel } from '../../../models/IWizardDataModel';
import WizardSessionData from '../../../services/WizardSessionData';
import fonts from '../view/fonts/fonts';

class DesignModel extends AbstractSessionModel implements IWizardDataModel {
  @Observable()
  public colorThemeStateMachine = new ColorThemeStateMachine();

  @StoreFieldInSession({
    serialize: (value: any) => {
      let serialized = value.map((logo: any) => {
        return logo.serialize();
      });

      return JSON.stringify(serialized);
    },
    deserialize(value: any) {
      let deserialized = JSON.parse(value);

      let logos = deserialized.map((object: any) => {
        object = JSON.parse(object);

        let objectType = object.objectType;
        if (objectType === 'FoundWebsiteLogo') {
          return new FoundWebsiteLogo(
            object.value.url,
            object.value.colors,
            object.value.dimensions
          );
        } else if (objectType === 'UploadedWebsiteLogo') {
          return new UploadedWebsiteLogo(
            object.value.file,
            object.value.colors,
            object.value.dimensions
          );
        }
      });

      return logos;
    }
  })
  @Observable()
  public foundLogos: Array<ImageWebsiteLogo> = [];

  @StoreFieldInSession({
    serialize: (value: any) => {
      let serialized = value.map((logo: any) => logo.serialize());
      return JSON.stringify(serialized);
    },
    deserialize(value: any) {
      if (!value) return [];
      let deserialized = JSON.parse(value);
      return deserialized.map((object: any) => {
        object = JSON.parse(object);
        let objectType = object.objectType;
        if (objectType === 'FoundWebsiteLogo') {
          return new FoundWebsiteLogo(
            object.value.url,
            object.value.colors,
            object.value.dimensions
          );
        } else if (objectType === 'UploadedWebsiteLogo') {
          return new UploadedWebsiteLogo(
            object.value.file,
            object.value.colors,
            object.value.dimensions
          );
        }
      });
    }
  })
  @Observable()
  public allFoundLogos: Array<ImageWebsiteLogo> = [];

  // How many scraped logos are currently revealed (initial step = 5)
  @StoreFieldInSession()
  @Observable()
  public foundLogosVisibleCount: number = 5;

  @StoreFieldInSession({
    serialize: (value: any) => {
      let serialized = value.map((logo: any) => {
        return logo.serialize();
      });

      return JSON.stringify(serialized);
    },
    deserialize(value: any) {
      let deserialized = JSON.parse(value);

      let logos = deserialized.map((object: any) => {
        object = JSON.parse(object);

        let objectType = object.objectType;
        if (objectType === 'FoundWebsiteLogo') {
          return new FoundWebsiteLogo(
            object.value.url,
            object.value.colors,
            object.value.dimensions
          );
        } else if (objectType === 'UploadedWebsiteLogo') {
          return new UploadedWebsiteLogo(
            object.value.file,
            object.value.colors,
            object.value.dimensions
          );
        }
      });

      return logos;
    }
  })
  @Observable()
  public uploadedLogos: Array<ImageWebsiteLogo> = [];

  @StoreFieldInSession({
    serialize: (value: any) => {
      return value?.serialize() || null;
    },
    deserialize(value: any) {
      if (value === null) {
        return null;
      }

      let object = JSON.parse(value);
      let objectType = object.objectType;

      if (objectType === 'FoundWebsiteLogo') {
        return new FoundWebsiteLogo(
          object.value.url,
          object.value.colors,
          object.value.dimensions
        );
      } else if (objectType === 'UploadedWebsiteLogo') {
        return new UploadedWebsiteLogo(
          object.value.file,
          object.value.colors,
          object.value.dimensions
        );
      }
    }
  })
  @Observable()
  public selectedLogo: ImageWebsiteLogo | null = null;

  @StoreFieldInSession()
  @Observable()
  selectedFontId: string = '1';

  @StoreFieldInSession()
  @Observable()
  selectedFontData = fonts.find((f) => f.id === '1');

  @Observable()
  public loadingLogo: boolean = false;

  @Observable()
  public isConfirmationModalVisible: boolean = false;

  @Observable()
  public designModelWizardData = new DesignModelWizardData();

  constructor() {
    super();
    super.initFields();
  }

  @Action()
  setSelectedFontId(fontId: string) {
    this.selectedFontId = fontId;
    this.selectedFontData = fonts.find((f) => f.id === fontId);
  }

  @Action()
  public changeSelectedLogo(logo: ImageWebsiteLogo) {
    this.selectedLogo = logo;

    if (logo) {
      this.designModelWizardData.logo = logo.getFileUrl();
      this.colorThemeStateMachine.transition('selectLogo', {
        colorSchema: this.selectedLogo.getColors()
      });
    } else {
      this.designModelWizardData.logo = '';
      this.colorThemeStateMachine.transition('clearLogo', {});
    }
  }

  getSelectedLogoPrimaryColor() {
    return this.selectedLogo?.getColors().primary.main;
  }

  @Action()
  setPrimaryColor(color: string, newColorSchema?: ColorScheme) {
    this.colorThemeStateMachine.transition('changedPrimaryColorByUser', {
      primaryColor: color,
      newColorSchema
    });
  }

  @Action()
  setSecondaryColor(color: string) {
    this.colorThemeStateMachine.transition('changedSecondaryColorByUser', {
      secondaryColor: color
    });
  }

  @Computed()
  get primaryColor() {
    return this.colorThemeStateMachine.primaryColor;
  }

  @Computed()
  get secondaryColor() {
    return this.colorThemeStateMachine.secondaryColor;
  }

  @Action()
  async extractWebsiteLogo(websiteUrl: string, category: string) {
    try {
      this.loadingLogo = true;
      const response = await Api.extractWebsiteLogo(websiteUrl, category);

      runInAction(() => {
        const all = response.possibleImages.map((data) => {
          return new FoundWebsiteLogo(
            {
              ObjectURL: data.ObjectURL,
              PresignedUrl: data.PresignedUrl
            },
            data.colors,
            {
              width: data.width,
              height: data.height
            }
          );
        });
        this.allFoundLogos = all;
        this.foundLogosVisibleCount = 5;
        this.foundLogos = all.slice(0, this.foundLogosVisibleCount);

        if (this.foundLogos.length > 0) {
          // make the first logo selected by default
          this.changeSelectedLogo(this.foundLogos[0]);
        }
      });
    } catch (error) {
      runInAction(() => {
        this.foundLogos = [];
        this.allFoundLogos = [];
        this.foundLogosVisibleCount = 5;
      });
    } finally {
      runInAction(() => {
        this.loadingLogo = false;
      });
    }
  }

  @Action()
  public loadMoreLogos(step: number = 5) {
    if (!this.allFoundLogos || this.allFoundLogos.length === 0) return;
    const next = Math.min(
      this.foundLogosVisibleCount + step,
      this.allFoundLogos.length
    );
    this.foundLogosVisibleCount = next;
    this.foundLogos = this.allFoundLogos.slice(0, this.foundLogosVisibleCount);
  }

  @Action()
  public openColorThemeConfirmationModal() {
    this.isConfirmationModalVisible = true;
  }

  @Action()
  public closeColorThemeConfirmationModal() {
    this.isConfirmationModalVisible = false;
  }

  @Action()
  public setUploadedLogs(logos: Array<ImageWebsiteLogo>) {
    this.uploadedLogos = logos;
  }

  @Computed()
  get getFinalColorScheme(): ColorScheme {
    return this.colorThemeStateMachine.colorSchema;
  }

  getWizardData() {
    return this.designModelWizardData.getWizardData();
  }

  public getSessionKey(): string {
    return 'DesignModel';
  }
}

const model = new DesignModel();
WizardSessionData.addModel(model);
export default model;
